import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { type TranslationKey, loadTranslations, translations } from "./translations";
import { 
  SUPPORTED_LANGUAGES, 
  Language, 
  STORAGE_KEY, 
  getInitialLanguage 
} from "./constants";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [language, setLanguageState] = useState<Language>(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang as Language)) {
      return lang as Language;
    }
    return getInitialLanguage();
  });

  const [, setLoaded] = useState(false);

  // Preload translations for current language
  useEffect(() => {
    loadTranslations(language).then(() => setLoaded(prev => !prev));
  }, [language]);

  // Sync language with URL parameter
  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang as Language)) {
      if (lang !== language) {
        setLanguageState(lang as Language);
        localStorage.setItem(STORAGE_KEY, lang);
      }
    }
  }, [lang, language]);

  // Redirect root to language-prefixed URL
  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "") {
      const targetLang = getInitialLanguage();
      navigate(`/${targetLang}${location.hash}`, { replace: true });
    }
  }, [location.pathname, location.hash, navigate]);

  const setLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
    
    const pathParts = location.pathname.split('/');
    const pathWithoutLang = pathParts.slice(2).join('/');
    const newPath = pathWithoutLang 
      ? `/${newLang}/${pathWithoutLang}${location.hash}`
      : `/${newLang}${location.hash}`;
    
    navigate(newPath, { replace: true });
  };

  const t = (key: TranslationKey): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export { SUPPORTED_LANGUAGES, type Language };
